import { useEffect } from "react";
import { X, Calendar, CheckCircle2, Sparkles, ArrowUpRight, Target } from "lucide-react";
import { useDemo } from "@/demo/DemoContext";

const SLOTS = [
  { id: "slot-1", day: "Thu", date: "Jun 19", time: "2:00 pm" },
  { id: "slot-2", day: "Thu", date: "Jun 19", time: "3:30 pm" },
  { id: "slot-3", day: "Fri", date: "Jun 20", time: "10:00 am" },
  { id: "slot-4", day: "Fri", date: "Jun 20", time: "1:00 pm" },
];

export default function BookingModal() {
  const { bookingOpen, closeBooking } = useDemo();

  useEffect(() => {
    if (!bookingOpen) return;
    const onKey = (e) => e.key === "Escape" && closeBooking();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [bookingOpen, closeBooking]);

  if (!bookingOpen) return null;

  return (
    <div
      data-testid="booking-modal"
      onClick={closeBooking}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(6, 12, 20, 0.72)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        animation: "enterUp 200ms ease-out",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card-glass"
        style={{
          width: "100%",
          maxWidth: 640,
          maxHeight: "92vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          background:
            "radial-gradient(ellipse 500px 220px at 50% -20%, rgba(0, 194, 168, 0.16) 0%, transparent 70%), var(--bg-elevated)",
          border: "1px solid rgba(0, 194, 168, 0.30)",
          boxShadow: "0 24px 64px rgba(0, 0, 0, 0.55), 0 0 40px rgba(0, 194, 168, 0.15)",
        }}
      >
        {/* Header */}
        <div
          className="flex items-start justify-between px-6 pt-6 pb-4"
          style={{ borderBottom: "1px solid var(--border-divider)" }}
        >
          <div>
            <div
              className="inline-flex items-center gap-2 rounded-full mb-3"
              style={{
                padding: "3px 10px",
                background: "rgba(0, 194, 168, 0.10)",
                border: "1px solid rgba(0, 194, 168, 0.30)",
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 9.5,
                letterSpacing: "0.14em",
                color: "#00C2A8",
                textTransform: "uppercase",
              }}
            >
              <Sparkles size={10} />
              Growth Audit
            </div>
            <h2
              style={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 800,
                fontSize: 22,
                letterSpacing: "-0.02em",
                color: "var(--text-primary)",
                lineHeight: 1.2,
                marginBottom: 6,
              }}
            >
              Book Your 30-Minute Growth Audit
            </h2>
            <p
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: 13,
                lineHeight: 1.55,
                color: "var(--text-secondary)",
                maxWidth: 520,
              }}
            >
              We'll map your current MQL-to-SQL gap and show you what Tier 2 looks like built on your actual lead data.
            </p>
          </div>
          <button
            data-testid="booking-close"
            onClick={closeBooking}
            className="flex items-center justify-center rounded-md flex-shrink-0"
            style={{
              width: 30,
              height: 30,
              background: "var(--bg-elevated)",
              border: "1px solid var(--border-divider)",
              color: "var(--text-secondary)",
              cursor: "pointer",
              marginLeft: 12,
            }}
          >
            <X size={14} />
          </button>
        </div>

        {/* Scroll body */}
        <div style={{ overflowY: "auto", padding: "20px 24px" }}>
          {/* Calendar mock */}
          <div className="mb-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Calendar size={14} color="#00C2A8" />
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 10.5,
                    color: "var(--text-muted)",
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                  }}
                >
                  Pick a 30-min slot
                </span>
              </div>
              <span
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 10,
                  color: "#00C2A8",
                  padding: "2px 8px",
                  borderRadius: 4,
                  background: "rgba(0, 194, 168, 0.10)",
                  border: "1px solid rgba(0, 194, 168, 0.30)",
                }}
              >
                Powered by Cal.com
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              {SLOTS.map((s, i) => (
                <button
                  key={s.id}
                  data-testid={s.id}
                  className="text-left rounded-lg flex flex-col enter-up"
                  style={{
                    animationDelay: `${i * 40}ms`,
                    padding: 12,
                    background: "rgba(15, 25, 35, 0.5)",
                    border: "1px solid var(--border-divider)",
                    color: "var(--text-primary)",
                    cursor: "pointer",
                    transition: "all 150ms ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "rgba(0, 194, 168, 0.45)";
                    e.currentTarget.style.background = "rgba(0, 194, 168, 0.08)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "var(--border-divider)";
                    e.currentTarget.style.background = "rgba(15, 25, 35, 0.5)";
                  }}
                >
                  <span
                    style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 9.5,
                      letterSpacing: "0.12em",
                      color: "var(--text-muted)",
                      textTransform: "uppercase",
                    }}
                  >
                    {s.day} · {s.date}
                  </span>
                  <span
                    style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontWeight: 600,
                      fontSize: 15,
                      color: "var(--text-primary)",
                      marginTop: 3,
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {s.time}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* What you'll get */}
          <div
            className="rounded-lg p-4 mb-4"
            style={{
              background: "rgba(15, 25, 35, 0.5)",
              border: "1px solid var(--border-divider)",
            }}
          >
            <div
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 10,
                letterSpacing: "0.14em",
                color: "#00C2A8",
                textTransform: "uppercase",
                marginBottom: 10,
              }}
            >
              What you'll get
            </div>
            <ul className="space-y-2">
              {[
                "A diagnosis of your current conversion gap",
                "A recommended sequence structure for your lead volume",
                "A Tier recommendation (Tier 1 / Tier 2 / Tier 3) with a build estimate",
              ].map((it, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle2 size={13} color="#00C2A8" style={{ marginTop: 2, flexShrink: 0 }} />
                  <span
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontSize: 12.5,
                      color: "var(--text-primary)",
                      lineHeight: 1.5,
                    }}
                  >
                    {it}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* ROI line */}
          <div
            className="rounded-lg p-3.5 flex items-start gap-2.5"
            style={{
              background:
                "linear-gradient(135deg, rgba(0, 194, 168, 0.08) 0%, rgba(14, 165, 233, 0.05) 100%)",
              border: "1px solid rgba(0, 194, 168, 0.25)",
            }}
          >
            <Target size={14} color="#00C2A8" style={{ marginTop: 2, flexShrink: 0 }} />
            <p
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: 12.5,
                lineHeight: 1.55,
                color: "var(--text-primary)",
                margin: 0,
              }}
            >
              Clients on Tier 2 recover{" "}
              <span style={{ color: "#00C2A8", fontWeight: 600 }}>17× their investment</span> in the first quarter from leads that would otherwise cost{" "}
              <span style={{ color: "#F59E0B", fontWeight: 600 }}>$162–$286 each</span> and go cold.
            </p>
          </div>
        </div>

        {/* Footer CTA */}
        <div
          className="px-6 py-4 flex items-center justify-between"
          style={{
            borderTop: "1px solid var(--border-divider)",
            background: "rgba(15, 25, 35, 0.5)",
          }}
        >
          <span
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 10.5,
              color: "var(--text-muted)",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}
          >
            No sales pitch · Your data
          </span>
          <button
            data-testid="booking-confirm"
            onClick={closeBooking}
            className="inline-flex items-center gap-2 rounded-lg"
            style={{
              padding: "10px 18px",
              background: "linear-gradient(135deg, #00C2A8 0%, #0EA5E9 100%)",
              color: "#0F1923",
              fontFamily: "Inter, sans-serif",
              fontSize: 13.5,
              fontWeight: 700,
              letterSpacing: "-0.005em",
              border: "none",
              cursor: "pointer",
              boxShadow: "0 0 24px rgba(0, 194, 168, 0.35)",
              transition: "transform 150ms ease, box-shadow 150ms ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow = "0 4px 32px rgba(0, 194, 168, 0.55)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 0 24px rgba(0, 194, 168, 0.35)";
            }}
          >
            Confirm Booking
            <ArrowUpRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
